import { useCallback, useEffect, useMemo } from 'react';
import { Panel, RadioGroup, TextArea } from '@epam/promo';

import { setAssignedGroup, useTaskStore } from 'store/task';
import { useTaskGroupsStore } from 'store/task-groups';
import {
  AISuggestionWrapper,
  AISuggestionWrapperChildrenType,
  TABS,
} from 'components/common/AISuggestionWrapper';
import { useAppLocation } from 'hooks/helpers';
import { taskRoutes } from 'router/taskRoutes';
import { FileLoader } from '../../recognitions/FormBody/components';
import { RadioGroup as CustomRadioGroup } from './components';
import css from './FormBody.module.scss';
import { InlineLabelInput } from '../../common';

export const FormBody = () => {
  const { isCreate } = useAppLocation(taskRoutes);
  const {
    errors,
    draft: draftTask,
    assignedGroup,
  } = useTaskStore((state) => state);
  const taskGroups = useTaskGroupsStore((state) => state.taskGroups);

  const {
    setDraftTitle,
    setDraftDescription,
    setDraftPictures,
    setDraftErrorMessage,
    setDraftSuccessMessage,
    setDraftComment,
    setDraftGroupId,
  } = useTaskStore((state) => state.actions);

  const handleGroupSelect = useCallback(
    (newVal: string) => {
      setAssignedGroup(newVal);
      setDraftGroupId();
    },
    [setDraftGroupId],
  );

  useEffect(() => {
    if (!draftTask.taskGroupId && taskGroups.length) {
      handleGroupSelect(taskGroups[0].id);
    }
  }, [draftTask.taskGroupId, taskGroups, handleGroupSelect]);

  const isReadonly = useMemo(
    () =>
      draftTask.status === 'PENDING_APPROVAL' ||
      draftTask.status === 'APPROVED',
    [draftTask.status],
  );

  const getDescription: AISuggestionWrapperChildrenType = ({
    onBlur,
    onFocus,
    isAISuggestionBlockShown,
  }) => (
    <div key={draftTask.id}>
      <TextArea
        autoSize
        inputCx={{
          [css.textarea__input]: true,
          [css['textarea__input--error']]: errors.description,
          [css['textarea__input--disabled']]: isAISuggestionBlockShown,
        }}
        isDisabled={isReadonly || isAISuggestionBlockShown}
        isInvalid={Boolean(errors.description)}
        placeholder="Add description"
        rawProps={{ 'data-testid': 'draftTask-description-input' }}
        size="48"
        value={draftTask?.description}
        onBlur={(event) => {
          setDraftDescription(event.target.value.trim(), {
            markAsDirty: false,
            shouldValidate: true,
          });
          onBlur(event);
        }}
        onFocus={onFocus}
        onValueChange={setDraftDescription}
      />
    </div>
  );

  return (
    <Panel
      key={draftTask.id}
      cx={{
        [css.form]: true,
        [css.form__disabled]: isReadonly,
      }}>
      <InlineLabelInput
        isRequired
        isTitle
        errorText={errors.title}
        isReadonly={isReadonly}
        label="Title"
        readonlyValue={draftTask.title ?? ''}>
        <TextArea
          autoSize
          isRequired
          autoFocus={isCreate}
          inputCx={{
            [css.textarea__input]: true,
            [css.textarea__title]: draftTask.title,
            [css['textarea__input--error']]: !!errors.title,
          }}
          isInvalid={Boolean(errors.title)}
          placeholder="Add title"
          rawProps={{ 'data-testid': 'draftTask-title-input' }}
          size="48"
          value={draftTask.title}
          onBlur={({ target: { value } }) =>
            setDraftTitle(value.trim(), {
              markAsDirty: false,
              shouldValidate: true,
            })
          }
          onValueChange={setDraftTitle}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.description}
        isReadonly={isReadonly}
        label="Description"
        readonlyValue={draftTask.description ?? ''}>
        <AISuggestionWrapper
          options={{
            tab: TABS.TASKS,
            field: 'description',
          }}
          setValue={setDraftDescription}
          value={draftTask.description}>
          {getDescription}
        </AISuggestionWrapper>
      </InlineLabelInput>

      <InlineLabelInput align="top" label="Your files">
        <FileLoader
          attachments={draftTask.attachments}
          isReadonly={isReadonly}
          setDraftPictures={setDraftPictures}
        />
      </InlineLabelInput>

      <CustomRadioGroup task={draftTask} />

      <InlineLabelInput
        isOptional
        errorText={errors.errorMessage}
        isReadonly={isReadonly}
        label="Error text"
        readonlyValue={draftTask.errorMessage ?? ''}>
        <TextArea
          autoSize
          inputCx={{
            [css.textarea__input]: true,
            [css['textarea__input--error']]:
              draftTask.errorMessage.length > 0 && errors.errorMessage,
          }}
          isDisabled={isReadonly}
          isInvalid={Boolean(errors.errorMessage)}
          placeholder={isReadonly ? 'No error message' : 'Add error message'}
          rawProps={{ 'data-testid': 'error-msg-input' }}
          size="48"
          value={draftTask.errorMessage}
          onBlur={({ target: { value } }) =>
            setDraftErrorMessage(value.trim(), {
              markAsDirty: false,
              shouldValidate: true,
            })
          }
          onValueChange={setDraftErrorMessage}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isOptional
        errorText={errors.successMessage}
        isReadonly={isReadonly}
        label="Success message"
        readonlyValue={draftTask.successMessage ?? ''}>
        <TextArea
          autoSize
          inputCx={{
            [css.textarea__input]: true,
            [css['textarea__input--error']]:
              draftTask.successMessage?.length > 0 && errors.successMessage,
            [css.form__input_readonly]: isReadonly,
          }}
          isDisabled={isReadonly}
          isInvalid={Boolean(errors.successMessage)}
          placeholder={
            isReadonly ? 'No success message' : 'Add success message'
          }
          rawProps={{ 'data-testid': 'success-msg-input' }}
          size="48"
          value={draftTask.successMessage}
          onBlur={({ target: { value } }) =>
            setDraftSuccessMessage(value.trim(), {
              markAsDirty: false,
              shouldValidate: true,
            })
          }
          onValueChange={setDraftSuccessMessage}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isOptional
        errorText={errors.comment}
        isReadonly={isReadonly}
        label="Comment"
        readonlyValue={draftTask.comment ?? ''}>
        <TextArea
          autoSize
          inputCx={{
            [css.textarea__input]: true,
            [css['textarea__input--error']]:
              draftTask.comment.length > 0 && errors.comment,
          }}
          isDisabled={isReadonly}
          isInvalid={Boolean(errors.comment)}
          placeholder={isReadonly ? 'No comment' : 'Add comment'}
          rawProps={{ 'data-testid': 'comment-input' }}
          size="48"
          value={draftTask.comment}
          onBlur={({ target: { value } }) => setDraftComment(value.trim())}
          onValueChange={setDraftComment}
        />
      </InlineLabelInput>

      <InlineLabelInput isRequired align="top" label="Groups">
        <RadioGroup
          cx={{
            [css.radio__container]: true,
            [css.form__input_readonly]: isReadonly,
          }}
          direction="vertical"
          isDisabled={isReadonly}
          items={taskGroups}
          rawProps={{
            'data-testid': isReadonly
              ? 'groups-readonly-input'
              : 'groups-input',
          }}
          value={assignedGroup}
          onValueChange={(newVal) => {
            if (newVal) {
              handleGroupSelect(newVal);
            }
          }}
        />
      </InlineLabelInput>
    </Panel>
  );
};
